import { SplitSupplyDayType } from '@ebalance/proto/bffuser/v1/split_supply_contract_model_pb'

import { KOMA_COUNT } from '~/constants/koma'
import {
  type CivilDate,
  compareCivilDate,
  todayCivilDate,
} from '~/models/civilDate'
import { parseTsv, planPaste } from '~/models/gridSelection'

/**
 * 基本パターン (通告値の初期値となる 48 コマの値) の画面モデル。
 * 値は kWh/30 分、 0 以上の整数 (BP 標準 JP06357 N(9))。 判断の正は backend の
 * entity で、 ここの検査は入力中に理由を出すための事前チェック (同じ理由を出す)。
 */

/** 通告値の上限 (BP 標準 JP06357 N(9))。 */
export const MAX_SLOT_VALUE = 999_999_999

/** 日種の並び (画面表示順)。 */
export const DAY_TYPES = [
  SplitSupplyDayType.WEEKDAY,
  SplitSupplyDayType.SATURDAY,
  SplitSupplyDayType.SUNDAY,
  SplitSupplyDayType.HOLIDAY,
] as const

/** 契約フォームが持つ基本パターン 1 件 (保存される形)。 */
export type BasePatternValue = {
  patternOrder: number
  validFrom: CivilDate
  validTo: CivilDate | undefined
  dayTypes: SplitSupplyDayType[]
  /** 1〜12。 空 = 全月。 */
  months: number[]
  /** コマ index (0〜47)。 両方 undefined = 終日。 */
  slotFrom: number | undefined
  slotTo: number | undefined
  /** 48 個。 */
  slotValues: number[]
}

/** 基本パターンモーダルの入力途中の状態 (数値は入力文字列のまま持つ)。 */
export type PatternDraft = {
  patternOrder: string
  validFrom: CivilDate
  validTo: CivilDate | undefined
  dayTypes: SplitSupplyDayType[]
  months: number[]
  /** '' = 終日 (その端を指定しない)。 */
  slotFrom: string
  slotTo: string
  valueMode: 'uniform' | 'perSlot'
  uniformValue: string
  slotValues: string[]
}

export const DEFAULT_PATTERN_ORDER_STEP = 10
/** API の pattern_order は int32。 */
const MAX_PATTERN_ORDER = 2_147_483_647

/** 既存の最大 + 10 (空なら 10)。 int32 の上限を超える場合は未設定。 */
export function nextPatternOrder(
  patterns: readonly BasePatternValue[],
): number | undefined {
  const max = patterns.reduce((m, p) => Math.max(m, p.patternOrder), 0)
  const next = max + DEFAULT_PATTERN_ORDER_STEP
  return next <= MAX_PATTERN_ORDER ? next : undefined
}

/** 新規追加の既定値: 開始日 今日・日種 すべて・全月・終日・全コマ同値 (未入力)。 */
export function newPatternDraft(
  patterns: readonly BasePatternValue[],
): PatternDraft {
  return {
    patternOrder: nextPatternOrder(patterns)?.toString() ?? '',
    validFrom: todayCivilDate(),
    validTo: undefined,
    dayTypes: [...DAY_TYPES],
    months: [],
    slotFrom: '',
    slotTo: '',
    valueMode: 'uniform',
    uniformValue: '',
    slotValues: Array.from({ length: KOMA_COUNT }, () => ''),
  }
}

/** 全コマが同じ値か。 保存済みの 48 個から「全コマ同値」表示に戻す判定。 */
export function isUniformValues(values: readonly number[]): boolean {
  return values.length > 0 && values.every((v) => v === values[0])
}

export function draftFromPattern(p: BasePatternValue): PatternDraft {
  const uniform = isUniformValues(p.slotValues)
  return {
    patternOrder: String(p.patternOrder),
    validFrom: p.validFrom,
    validTo: p.validTo,
    dayTypes: [...p.dayTypes],
    months: [...p.months],
    slotFrom: p.slotFrom === undefined ? '' : String(p.slotFrom),
    slotTo: p.slotTo === undefined ? '' : String(p.slotTo),
    valueMode: uniform ? 'uniform' : 'perSlot',
    uniformValue: uniform ? String(p.slotValues[0]) : '',
    slotValues: p.slotValues.map(String),
  }
}

/**
 * セル文字列を 0 以上の整数にする (前後の空白と桁区切りの ',' を除く)。
 * 空・負・小数・999,999,999 超・数字以外は undefined。
 */
export function parseSlotValue(raw: string): number | undefined {
  const text = raw.trim().replace(/,/g, '')
  if (!/^\d+$/.test(text)) return undefined
  const n = Number(text)
  return n <= MAX_SLOT_VALUE ? n : undefined
}

export type PatternDraftIssue =
  | { kind: 'orderInvalid' }
  | { kind: 'orderTooLarge' }
  | { kind: 'orderDuplicated'; order: number }
  | { kind: 'dayTypesRequired' }
  | { kind: 'periodInvalid' }
  | { kind: 'slotRangeInvalid' }
  | { kind: 'uniformValueInvalid' }
  /** 値が不正な最初の表の行 (0〜47)。 時刻で案内するための index。 */
  | { kind: 'slotValueInvalid'; slotIndex: number }

export type PatternDraftResult =
  | { ok: true; value: BasePatternValue }
  | { ok: false; issues: PatternDraftIssue[] }

/**
 * 入力途中の状態を検査して保存される形にする。 backend の entity と同じ理由で弾く
 * (適用優先順の重複・日種 0 個・終了日 < 開始日・値の空欄 / 負数 / 小数)。
 * `otherOrders` = 同じ契約の他の基本パターンの適用優先順 (編集中の自分は含めない)。
 */
export function validatePatternDraft(
  draft: PatternDraft,
  otherOrders: readonly number[],
): PatternDraftResult {
  const issues: PatternDraftIssue[] = []

  const orderText = draft.patternOrder.trim()
  const order = /^\d+$/.test(orderText) ? Number(orderText) : undefined
  if (order === undefined || order < 1) {
    issues.push({ kind: 'orderInvalid' })
  } else if (!Number.isSafeInteger(order) || order > MAX_PATTERN_ORDER) {
    issues.push({ kind: 'orderTooLarge' })
  } else if (otherOrders.includes(order)) {
    issues.push({ kind: 'orderDuplicated', order })
  }

  if (draft.dayTypes.length === 0) issues.push({ kind: 'dayTypesRequired' })

  if (
    draft.validTo !== undefined &&
    compareCivilDate(draft.validTo, draft.validFrom) < 0
  ) {
    issues.push({ kind: 'periodInvalid' })
  }

  if (
    draft.slotFrom !== '' &&
    draft.slotTo !== '' &&
    Number(draft.slotFrom) > Number(draft.slotTo)
  ) {
    issues.push({ kind: 'slotRangeInvalid' })
  }

  let slotValues: number[] = []
  if (draft.valueMode === 'uniform') {
    const v = parseSlotValue(draft.uniformValue)
    if (v === undefined) {
      issues.push({ kind: 'uniformValueInvalid' })
    } else {
      slotValues = Array.from({ length: KOMA_COUNT }, () => v)
    }
  } else {
    const parsed = draft.slotValues.map(parseSlotValue)
    const firstBad = parsed.findIndex((v) => v === undefined)
    if (firstBad >= 0 || parsed.length !== KOMA_COUNT) {
      issues.push({
        kind: 'slotValueInvalid',
        slotIndex: Math.max(firstBad, 0),
      })
    } else {
      slotValues = parsed as number[]
    }
  }

  if (issues.length > 0 || order === undefined) return { ok: false, issues }

  const slotFrom = draft.slotFrom === '' ? undefined : Number(draft.slotFrom)
  const slotTo = draft.slotTo === '' ? undefined : Number(draft.slotTo)
  return {
    ok: true,
    value: {
      patternOrder: order,
      validFrom: draft.validFrom,
      validTo: draft.validTo,
      dayTypes: DAY_TYPES.filter((d) => draft.dayTypes.includes(d)),
      months: [...draft.months].sort((a, b) => a - b),
      slotFrom,
      slotTo,
      slotValues,
    },
  }
}

export type SlotPastePlan = {
  /** 表の行 index と入れる文字列。 */
  cells: Array<{ index: number; value: string }>
  /** 数値にならず反映しなかったセルの数 (通知する)。 */
  skippedCount: number
}

/**
 * 48 コマの表への貼り付けを、 選んだ行から下へ入れる値の並びにする。
 * Excel の 1 列の縦貼り (改行区切り) も 1 行の横貼り (タブ区切り) も同じ並びとして受け、
 * 48 行目より先は切り捨てる。 数値にならないセル (空欄・文字列) は反映せず元の値を残し、
 * その数を返す (conventions の 48 コマのグリッドの貼り付け)。
 */
export function planSlotPaste(text: string, startRow: number): SlotPastePlan {
  const parsed = parseTsv(text)
  // 1 行だけの横貼りは縦に置き直す (1 列として扱う)
  const matrix =
    parsed.length === 1 && (parsed[0]?.length ?? 0) > 1
      ? (parsed[0] ?? []).map((cell) => [cell])
      : parsed
  const plan = planPaste(matrix, { row: startRow, col: 0 }, null, {
    rowCount: KOMA_COUNT,
    colCount: 1,
  })
  const cells: SlotPastePlan['cells'] = []
  let skippedCount = 0
  for (
    let row = plan.targetRange.rowMin;
    row <= plan.targetRange.rowMax;
    row++
  ) {
    const value = plan.sourceAt(row, 0)
    if (value === undefined) continue
    if (parseSlotValue(value) === undefined) {
      skippedCount += 1
    } else {
      cells.push({ index: row, value })
    }
  }
  return { cells, skippedCount }
}
